import { API_AUTH_BASE, API_PRIVATE_BASE } from "./utils/apiBase";

let authToken: string | null = null;

const readStoredToken = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem("authToken");
};

const getAuthToken = () => {
  if (authToken) {
    return authToken;
  }
  const stored = readStoredToken();
  authToken = stored || null;
  return authToken;
};

export const setAuthToken = (token: string | null | undefined) => {
  authToken = token || null;
  if (typeof window !== "undefined") {
    if (authToken) {
      window.localStorage.setItem("authToken", authToken);
    } else {
      window.localStorage.removeItem("authToken");
    }
  }
  return authToken;
};

export const clearAuthToken = () => setAuthToken(null);

const handleResponse = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401) {
      clearAuthToken();
    }
    const message = data?.message || "Wystąpił błąd serwera.";
    throw new Error(message);
  }
  return data;
};

const authorizedFetch = (path: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(`${API_PRIVATE_BASE}${path}`, {
    ...options,
    headers,
    credentials: "omit",
  });
};

export const fetchSession = async () => {
  if (!getAuthToken()) {
    return null;
  }
  const response = await authorizedFetch("/session");
  if (response.status === 204) {
    return null;
  }
  const data = await handleResponse(response);
  return data?.user ?? null;
};

export const login = async (credentials) => {
  const response = await fetch(`${API_AUTH_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(credentials),
  });
  const data = await handleResponse(response);
  if (data?.token) {
    setAuthToken(data.token);
  }
  return data.user;
};

export const logout = async () => {
  try {
    const response = await authorizedFetch("/session/logout", {
      method: "POST",
    });
    await handleResponse(response);
  } finally {
    clearAuthToken();
  }
};

export const register = async (payload) => {
  const response = await fetch(`${API_AUTH_BASE}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const updatePointContext = async (pointId) => {
  const response = await authorizedFetch("/points/context", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pointId }),
  });
  const data = await handleResponse(response);
  if (data?.token) {
    setAuthToken(data.token);
  }
  return data.user;
};

export const fetchProducts = async () => {
  const response = await authorizedFetch("/products");
  const data = await handleResponse(response);
  return data.products;
};

export const fetchProductRecommendations = async (
  productId,
  { limit = 3 } = {},
) => {
  const numericProductId = Number(productId);
  if (!Number.isInteger(numericProductId) || numericProductId <= 0) {
    return [];
  }

  const params = new URLSearchParams();
  const numericLimit = Number(limit);
  if (Number.isInteger(numericLimit) && numericLimit > 0) {
    params.set("limit", String(numericLimit));
  }

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const response = await authorizedFetch(
    `/products/${encodeURIComponent(numericProductId)}/recommendations${suffix}`,
  );
  const data = await handleResponse(response);
  if (!Array.isArray(data?.recommendations)) {
    return [];
  }
  return data.recommendations;
};

export const fetchProductGroups = async () => {
  const response = await authorizedFetch("/product-groups");
  const data = await handleResponse(response);
  return data.groups;
};

export const fetchMarketing = async () => {
  const response = await authorizedFetch("/marketing");
  const data = await handleResponse(response);
  return data.marketing;
};

export const submitOrder = async (payload) => {
  const response = await authorizedFetch("/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data.order;
};

export const fetchOrders = async () => {
  const response = await authorizedFetch("/orders");
  const data = await handleResponse(response);
  return data.orders;
};

export const checkOrderExists = async ({
  deliveryDate,
  kursId,
}: { deliveryDate?: string; kursId?: number | string } = {}) => {
  if (!deliveryDate) {
    return false;
  }
  const params = new URLSearchParams();
  params.set("deliveryDate", deliveryDate);
  if (kursId !== undefined && kursId !== null) {
    params.set("kursId", String(kursId));
  }
  const response = await authorizedFetch(`/orders/check?${params.toString()}`);
  const data = await handleResponse(response);
  return Boolean(data?.exists);
};

export const fetchSettlements = async () => {
  const response = await authorizedFetch("/settlements");
  const data = await handleResponse(response);
  return data.settlements;
};

export const fetchConfig = async () => {
  const response = await authorizedFetch("/config");
  return handleResponse(response);
};

export const fetchOrderDetails = async (orderId) => {
  const response = await authorizedFetch(
    `/orders/${encodeURIComponent(orderId)}`,
  );
  const data = await handleResponse(response);
  return data.order;
};

export const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_AUTH_BASE}/password/forgot`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return handleResponse(response);
};

export const resetPassword = async ({ token, password }) => {
  const response = await fetch(`${API_AUTH_BASE}/password/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  return handleResponse(response);
};
export const changePassword = async ({
  currentPassword,
  newPassword,
  confirmPassword,
}) => {
  const response = await authorizedFetch("/profile/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      currentPassword,
      newPassword,
      confirmPassword,
    }),
  });
  return handleResponse(response);
};
